
import React, { useState } from 'react';
import { authService } from '../lib/auth';
import { User } from '../types';

interface LoginModalProps {
  onLoginSuccess: (user: User | null) => void;
  onClose: () => void;
}

const LoginModal: React.FC<LoginModalProps> = ({ onLoginSuccess, onClose }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      console.log(`[Login] Pokus o přihlášení: ${email.trim()}`);
      await authService.login(email.trim(), password);
      // Success state is managed by listener in App.tsx
      onLoginSuccess(null); 
    } catch (err: any) {
      console.error("[Login] Selhání:", err.code, err.message);
      const code = err.code || '';
      
      if (code.includes('invalid-credential') || code.includes('user-not-found') || code.includes('wrong-password')) {
          setError('Neplatný e-mail nebo heslo. Zkontrolujte prosím své údaje.');
      } else if (code.includes('operation-not-allowed')) {
          setError('Metoda přihlášení není povolena v nastavení Firebase.');
      } else if (code.includes('too-many-requests')) {
          setError('Příliš mnoho neúspěšných pokusů. Zkuste to prosím za chvíli.');
      } else {
          setError(err.message || 'Přihlášení se nezdařilo.');
      }
    } finally {
      setLoading(false);
    }
  };

  const [linkNotice, setLinkNotice] = useState(false);
  const handleGoogle = async () => {
    setError(null);
    setLoading(true);
    try {
      const r = await authService.loginWithGoogle();
      if (r === 'link-needed') {
        setLinkNotice(true);
        return;
      }
      onLoginSuccess(null);
    } catch (err: any) {
      const code = err.code || '';
      if (code === 'auth/popup-closed-by-user' || code === 'auth/cancelled-popup-request') return;
      if (code === 'auth/operation-not-allowed') setError('Přihlášení přes Google zatím není ve Firebase zapnuté.');
      else if (code === 'auth/unauthorized-domain') setError('Tato adresa není ve Firebase povolená pro přihlášení přes Google.');
      else if (code === 'auth/popup-blocked') setError('Prohlížeč zablokoval přihlašovací okno. Povolte vyskakovací okna pro tuto stránku.');
      else setError(err.message || 'Přihlášení přes Google se nezdařilo.');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async () => {
    setError(null);
    if (!email || !password) {
        setError("Zadejte prosím e-mail a heslo.");
        return;
    }
    setLoading(true);
    try {
        await authService.register(email.trim(), password);
        onLoginSuccess(null);
    } catch (err: any) {
        console.error("[Register] Selhání:", err.code);
        if (err.code === 'auth/email-already-in-use') {
            setError('Uživatel s tímto e-mailem již existuje. Zkuste se přihlásit.');
        } else if (err.code === 'auth/weak-password') {
            setError('Heslo je příliš krátké (min. 6 znaků).');
        } else {
            setError(err.message || 'Registrace se nezdařila.');
        }
    } finally {
        setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[70] p-4" role="dialog" aria-modal="true">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden animate-fade-in">
        <div className="px-6 py-5 bg-gradient-to-r from-red-600 to-red-700 flex justify-between items-center text-white">
          <h2 className="text-xl font-bold">Administrace</h2>
          <button onClick={onClose} className="p-1 hover:bg-white/20 rounded-lg transition-colors">
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="p-8 space-y-5">
          {error && (
            <div className="p-4 bg-red-50 text-red-700 rounded-xl text-sm border border-red-200 flex items-start gap-3">
              <svg className="h-5 w-5 shrink-0" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" /></svg>
              <span>{error}</span>
            </div>
          )}
          
          {linkNotice && (
            <div className="p-4 bg-amber-50 text-amber-800 rounded-xl text-sm border border-amber-200">
              Tento e-mail už má účet s heslem. Přihlaste se jednou heslem níže. Google účet se tím propojí a příště stačí tlačítko Google.
            </div>
          )}

          <button type="button" onClick={handleGoogle} disabled={loading}
            className="w-full py-3 px-4 bg-white hover:bg-gray-50 text-gray-800 font-bold rounded-xl border-2 border-gray-200 flex items-center justify-center gap-3 transition-all active:scale-95 disabled:opacity-50">
            <svg className="w-5 h-5" viewBox="0 0 48 48" aria-hidden="true"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z"/><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/><path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-7.9l-6.5 5C9.5 39.6 16.2 44 24 44z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z"/></svg>
            Přihlásit přes Google
          </button>
          <div className="flex items-center gap-3 text-[10px] font-bold text-gray-400 uppercase tracking-widest"><div className="flex-1 h-px bg-gray-200" />nebo e-mailem<div className="flex-1 h-px bg-gray-200" /></div>

          <div className="space-y-1">
            <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider ml-1">E-mail</label>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-red-500 outline-none transition-all bg-gray-50"
              placeholder="vash@email.cz"
            />
          </div>
          
          <div className="space-y-1">
            <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider ml-1">Heslo</label>
            <input
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-red-500 outline-none transition-all bg-gray-50"
              placeholder="••••••••"
            />
          </div>

          <div className="flex gap-4 pt-2">
            <button
                type="submit"
                disabled={loading}
                className="flex-1 py-3 px-4 bg-red-600 hover:bg-red-700 text-white font-bold rounded-xl shadow-lg shadow-red-200 transition-all active:scale-95 disabled:opacity-50"
            >
                {loading ? 'Přihlašuji...' : 'Přihlásit se'}
            </button>
            <button
                type="button"
                onClick={handleRegister}
                disabled={loading}
                className="flex-1 py-3 px-4 bg-white hover:bg-gray-50 text-gray-700 font-bold rounded-xl border border-gray-200 transition-all active:scale-95 disabled:opacity-50"
            >
                Registrovat
            </button>
          </div>
          <p className="text-center text-[10px] text-gray-400">Přístup povolen pouze oprávněným osobám. Pokud máte zapnuté dvoufázové ověření, budete požádáni o kód z aplikace.</p>
        </form>
      </div>
    </div>
  );
};

export default LoginModal;
